import { useEffect } from 'react';
import {
  hashQueryKey,
  MutateOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useLatest } from 'hooks/useLatest';
import { getReviewId } from 'query/review';
import {
  createHighlight,
  deleteHighlight,
  getHighlights,
  HighlightsResult,
  HighlightType,
} from 'query/review/Highlights';

export const HIGHLIGHTS_ENABLED_STORAGE_KEY =
  'covidence.userPreferences.keywordHighlightingEnabled';

export type HighlightKeyword = {
  type: HighlightType;
  keyword: string;
};

type HighlightChangeCallbacks = MutateOptions<
  HighlightsResult,
  unknown,
  HighlightKeyword
>;

type UseHighlightsResult = {
  isLoading: boolean;
  hasHighlights: boolean;
  highlights?: HighlightsResult;
  isAddingHighlight: boolean;
  addHighlight: (
    change: HighlightKeyword,
    callbacks?: HighlightChangeCallbacks
  ) => void;
  removeHighlight: (
    change: HighlightKeyword,
    callbacks?: HighlightChangeCallbacks
  ) => void;
};

const highlightsQueryKey = () => ['highlights', getReviewId() || ''];

export type UseHighlightsOptions = { poll?: boolean };

const REFETCH_INTERVAL_MS = 10_000;
const STALE_TIME_MS = 10_000;

// poll: keep refetching while mounted. Only the study list sidebar asks for
// it, so pages that just read highlights (e.g. Review home / setup) don't poll.
export function useHighlights(
  options?: UseHighlightsOptions
): UseHighlightsResult {
  const queryClient = useQueryClient();
  const queryKey = highlightsQueryKey();

  const { isLoading, data: highlights } = useQuery({
    queryKey,
    queryFn: getHighlights,
    staleTime: STALE_TIME_MS,
    refetchInterval: options?.poll ? REFETCH_INTERVAL_MS : undefined,
  });

  const hasHighlights =
    (highlights?.includedKeywords.length ?? 0) > 0 ||
    (highlights?.excludedKeywords.length ?? 0) > 0;

  // The server answers with the review's full lists; they replace the cache.
  // Runs before React re-renders, so a caller's onSuccess still sees the pre-save hasHighlights.
  const replaceHighlights = (saved: HighlightsResult) => {
    // Cancel in-flight fetches first: one started before the write could
    // settle late and overwrite the newer snapshot.
    void queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, saved);
  };

  const adding = useMutation({
    mutationFn: ({ type, keyword }: HighlightKeyword) =>
      createHighlight(type, keyword),
    onSuccess: replaceHighlights,
  });

  const removing = useMutation({
    mutationFn: ({ type, keyword }: HighlightKeyword) =>
      deleteHighlight(type, keyword),
    onSuccess: replaceHighlights,
  });

  return {
    isLoading,
    hasHighlights: isLoading ? false : hasHighlights,
    highlights,
    isAddingHighlight: adding.isLoading,
    addHighlight: adding.mutate,
    removeHighlight: removing.mutate,
  };
}

// Fires for writes made through addHighlight / removeHighlight only, not background refetches,
// and synchronously before React re-renders, so the handler still sees the
// state from before the save. Prefer a per-call onSuccess when the
// listener is the saver's parent; this stays for the Backbone study list
// (studies_view.js onHighlightsChange), which cannot subscribe to the cache.
export function useOnHighlightsSaved(
  onSaved: (highlights: HighlightsResult) => void
): void {
  const queryClient = useQueryClient();
  const queryHash = hashQueryKey(highlightsQueryKey());
  const latestOnSaved = useLatest(onSaved);

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.query.queryHash !== queryHash)
          return;
        if (event.action.type !== 'success' || !event.action.manual) return;
        latestOnSaved.current(event.action.data as HighlightsResult);
      }),
    [queryClient, queryHash, latestOnSaved]
  );
}
